import { ErrorApi } from '../../../api/problema';
import type { Canal, Cliente, EstadoEntrega, EstadoPermiso, Importe, MotivoPuerta, OrigenFicha, OrigenPermiso, ResumenCliente } from '../../../api/ops/clientes';
import type { EstadoChip } from '../../../componentes/Chip';
import type { NombreIcono } from '../../../componentes/iconos';
import type { TarjetaAyuda } from '../../../marco/cabecera';

/**
 * Textos de la lista, el alta y la ficha de cliente (2.1 a 2.4), tal cual del diseño (`2.1-2.4-clientes-lista-alta-ficha.html`)
 * salvo donde manda una decisión: N1 a N3 de `modulos/clientes.md` §12 (N2 cambia), sin «Alergias» en ejemplos (G192),
 * ayuda de las notas (G194), Puerta con motivo de la lista cerrada y sin Verde (G193), bloqueo legal (G196) y la casilla
 * de la fecha de nacimiento (G241).
 */
export type PestanaFicha = 'resumen' | 'datos' | 'historial' | 'permisos' | 'notas' | 'entregas';

export const PESTANAS: readonly [PestanaFicha, string, NombreIcono][] = [
    ['resumen', 'Resumen', 'layout-grid'],
    ['datos', 'Datos', 'user'],
    ['historial', 'Historial', 'history'],
    ['permisos', 'Permisos', 'mail-check'],
    ['notas', 'Notas', 'sticky-note'],
    ['entregas', 'Entregas', 'send'],
];

/** N3, ayuda: «Si te da el permiso en persona…». */
export const N3_AYUDA = 'Si te da el permiso en persona, le enviamos un mensaje para que lo confirme. Hasta entonces no recibe campañas.';
/** N1 (campo teléfono del alta). */
export const N1_CAMPO = 'No se consulta su Cuenta Reservaria.';
export const N1 = 'Al teclear el teléfono no se consulta su Cuenta Reservaria: queda como dato de tu negocio hasta que el cliente lo verifique.';
/** N2 (cambia): la casilla del permiso comercial en el alta. */
export const N2_TITULO = 'Información comercial';
export const N2 = 'Márcala solo si el cliente te lo pide expresamente. Le llega un mensaje para confirmarlo y, hasta entonces, no recibe campañas.';
/** G194: ayuda de las notas (y de las evaluaciones de puerta). */
export const AYUDA_NOTAS = 'No escribas datos de salud, ideología, origen ni acusaciones. El cliente puede pedir una copia de estas notas.';
/** G241: casilla obligatoria para guardar la fecha de nacimiento (texto a validar por la asesoría, G262). */
export const CASILLA_NACIMIENTO = 'El cliente acepta que guardemos su fecha de nacimiento para felicitarle';

export const AYUDA: Record<'lista' | 'alta' | PestanaFicha, TarjetaAyuda[]> = {
    lista: [
        { icono: 'users', titulo: 'Tus clientes', texto: 'Cada negocio tiene su propia lista. Se crean solos con cada reserva, cita o entrada, o los das de alta tú.' },
        { icono: 'search', titulo: 'Buscar', texto: 'Por nombre, o por el teléfono o el email completos. Solo busca entre los clientes de tu negocio.' },
        { icono: 'badge-check', titulo: 'Cuenta Reservaria', texto: 'Si el cliente verifica su teléfono contigo, su ficha se vincula a su cuenta. Nombre, teléfono y email los cambia solo él.' },
    ],
    alta: [
        { icono: 'user-plus', titulo: 'Alta', texto: 'Basta con el nombre, el teléfono o el email. Si ya es cliente de tu negocio, te lleva a su ficha en vez de duplicarlo.' },
        { icono: 'shield', titulo: 'Su cuenta', texto: N1 },
        { icono: 'megaphone', titulo: N2_TITULO, texto: N2 },
    ],
    resumen: [
        { icono: 'layout-grid', titulo: 'Resumen', texto: 'Lo esencial del cliente en tu negocio: visitas, gasto, etiquetas y por dónde prefiere que le escribas.' },
        { icono: 'badge-check', titulo: 'Vinculada', texto: 'El cliente verificó su teléfono contigo. Sus datos de cuenta los cambia solo él.' },
    ],
    datos: [
        { icono: 'user', titulo: 'Datos de tu negocio', texto: 'El nombre con el que le conocéis y un contacto. Solo los ve tu negocio y no cambian su Cuenta Reservaria.' },
        { icono: 'pencil', titulo: 'Proponer una corrección', texto: 'Si su nombre o su email están mal, se lo propones. Lo acepta o no desde «Mis datos». Caduca a los 30 días.' },
    ],
    historial: [{ icono: 'history', titulo: 'Historial', texto: 'Todo lo que ha hecho en tu negocio, de lo más reciente a lo más antiguo, solo de los módulos que tienes contratados.' }],
    permisos: [
        { icono: 'mail-check', titulo: 'Permisos', texto: 'Confirmaciones, pases y recordatorios le llegan siempre. La información comercial, solo con su permiso.' },
        { icono: 'megaphone', titulo: 'Dado en persona', texto: N3_AYUDA },
        { icono: 'ban', titulo: 'Baja', texto: 'Una baja se aplica en el momento.' },
    ],
    notas: [
        { icono: 'sticky-note', titulo: 'Notas', texto: 'Solo las ve tu equipo. No se borran: se archivan.' },
        { icono: 'shield', titulo: 'Qué no escribir', texto: AYUDA_NOTAS },
    ],
    entregas: [
        { icono: 'send', titulo: 'Entregas', texto: 'Los mensajes que le ha enviado tu negocio, por qué canal y si le llegaron.' },
        { icono: 'refresh-cw', titulo: 'Reenviar', texto: 'Las confirmaciones y los pases se pueden reenviar si te dice que no le han llegado.' },
    ],
};

/** Los permisos de Clientes que miran estas pantallas (`x-permission` de `clientes.yaml` y `entregas.yaml`). */
export const PERMISOS = {
    ver: 'clientes.fichas.ver',
    crear: 'clientes.fichas.crear',
    editar: 'clientes.fichas.editar',
    notas: 'clientes.notas.escribir',
    proponer: 'clientes.correcciones.proponer',
    consentimientos: 'clientes.consentimientos.registrar',
    rgpd: 'clientes.rgpd.atender',
    importar: 'clientes.importaciones.gestionar',
    entregas: 'entregas.registro.ver',
    reenviar: 'entregas.entregas.reenviar',
} as const;

export const CANALES: Readonly<Record<Canal, { nombre: string; icono: NombreIcono }>> = {
    whatsapp: { nombre: 'WhatsApp', icono: 'message-circle' },
    email: { nombre: 'Email', icono: 'mail' },
    instagram: { nombre: 'Instagram', icono: 'instagram' },
    telegram: { nombre: 'Telegram', icono: 'send' },
    sms: { nombre: 'SMS', icono: 'smartphone' },
    voice: { nombre: 'Teléfono', icono: 'phone' },
};

/** «por WhatsApp», «por email». */
export function porCanal(canal: Canal): string {
    return canal === 'email' ? 'email' : CANALES[canal].nombre;
}

export const ORIGEN_FICHA: Readonly<Record<OrigenFicha, string>> = {
    panel: 'Alta en el panel',
    tablet: 'Alta en la tablet',
    web: 'Web pública',
    widget: 'Widget',
    chat: 'Chat',
    assistant: 'Asistente',
    list_link: 'Enlace de lista',
    import: 'Importación',
    migration_v1: 'Migración desde v1',
    api: 'Integración',
};

export const ORIGEN_PERMISO: Readonly<Record<OrigenPermiso, string>> = {
    web: 'En la web, al reservar',
    widget: 'En el widget, al reservar',
    chat: 'Por chat',
    assistant: 'Con el asistente',
    panel: 'En persona',
    tablet: 'En persona, en la tablet',
    list_link: 'Con el enlace de la lista',
    customer_account: 'Desde «Mis datos»',
    unsubscribe_link: 'Con el enlace de baja',
    consent_link: 'Desde el mensaje de confirmación',
    stop_keyword: 'Respondiendo BAJA',
    import: 'Importación',
    migration_v1: 'Migración desde v1',
    merge: 'Al unir fichas',
    api: 'Integración',
};

export const IDIOMAS: readonly { valor: string; texto: string }[] = [
    { valor: 'es-ES', texto: 'Español' },
    { valor: 'ca-ES', texto: 'Català' },
    { valor: 'en-GB', texto: 'English' },
    { valor: 'fr-FR', texto: 'Français' },
    { valor: 'de-DE', texto: 'Deutsch' },
];

export const ESTADO_AVISOS: Readonly<Record<EstadoPermiso, [string, EstadoChip]>> = {
    granted: ['Se envían', 'neutral'],
    none: ['Se envían', 'neutral'],
    withdrawn: ['Baja', 'problem'],
    pending_confirmation: ['Pendiente de confirmar', 'pending'],
};

export const ESTADO_COMERCIAL: Readonly<Record<EstadoPermiso, [string, EstadoChip]>> = {
    granted: ['Con permiso', 'confirmed'],
    pending_confirmation: ['Pendiente de confirmar', 'pending'],
    withdrawn: ['Baja', 'problem'],
    none: ['Sin permiso', 'neutral'],
};

export const DECISION_PERMISO: Readonly<Record<'granted' | 'withdrawn' | 'pending_confirmation', [string, EstadoChip]>> = {
    granted: ['Concedido', 'confirmed'],
    withdrawn: ['Baja', 'problem'],
    pending_confirmation: ['Pendiente de confirmar', 'pending'],
};

export const ESTADO_ENTREGA: Readonly<Record<EstadoEntrega, [string, EstadoChip]>> = {
    pending: ['En cola', 'pending'],
    queued: ['En cola', 'pending'],
    sent: ['Enviado', 'neutral'],
    delivered: ['Entregado', 'confirmed'],
    read: ['Leído', 'confirmed'],
    fallback: ['Por email', 'neutral'],
    failed: ['No ha llegado', 'problem'],
    discarded: ['No enviado', 'neutral'],
};

/** G193: motivos de la lista cerrada de las evaluaciones de puerta. */
export const MOTIVO_PUERTA: Readonly<Record<MotivoPuerta, string>> = {
    staff_behavior: 'Comportamiento con el personal',
    damage: 'Daños',
    non_payment: 'Impago',
    venue_rules: 'Incumplimiento de las normas del local',
    harassment: 'Acoso a otros clientes',
};

/** G193: solo se guardan ámbar y rojo (el verde quita la vigente). */
export const NIVEL_PUERTA: Readonly<Record<'amber' | 'red', [string, EstadoChip]>> = {
    amber: ['Ámbar', 'pending'],
    red: ['Rojo', 'problem'],
};

/** G196: el estado de una ficha bloqueada por obligación legal o reclamación. */
export const BLOQUEADA = 'Bloqueada por obligación legal';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];

function mismoDia(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** «Hoy», «Ayer» o «12 oct» (con el año si no es este). */
export function diaCorto(iso: string | null | undefined, ahora: Date = new Date()): string {
    if (iso == null) {
        return '';
    }
    const f = new Date(iso);
    if (Number.isNaN(f.getTime())) {
        return '';
    }
    const ayer = new Date(ahora);
    ayer.setDate(ahora.getDate() - 1);
    if (mismoDia(f, ahora)) {
        return 'Hoy';
    }
    if (mismoDia(f, ayer)) {
        return 'Ayer';
    }
    const base = `${String(f.getDate())} ${MES_CORTO[f.getMonth()] ?? ''}`;
    return f.getFullYear() === ahora.getFullYear() ? base : `${base} ${String(f.getFullYear())}`;
}

/** «Hoy, 10:02», «3 oct, 21:40». */
export function diaYHora(iso: string | null | undefined, ahora: Date = new Date()): string {
    if (iso == null) {
        return '';
    }
    const f = new Date(iso);
    if (Number.isNaN(f.getTime())) {
        return '';
    }
    const hora = `${String(f.getHours()).padStart(2, '0')}:${String(f.getMinutes()).padStart(2, '0')}`;
    return `${diaCorto(iso, ahora)}, ${hora}`;
}

/** «17 de mayo de 1991» de una fecha `AAAA-MM-DD` (o de un instante). */
export function fechaCompleta(valor: string | null | undefined): string {
    if (valor == null || valor === '') {
        return '';
    }
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
    if (m !== null) {
        return `${String(Number(m[3]))} de ${MESES[Number(m[2]) - 1] ?? ''} de ${m[1] ?? ''}`;
    }
    const f = new Date(valor);
    return Number.isNaN(f.getTime()) ? '' : `${String(f.getDate())} de ${MESES[f.getMonth()] ?? ''} de ${String(f.getFullYear())}`;
}

/** «marzo de 2025». */
export function mesYAnio(iso: string | null | undefined): string {
    if (iso == null) {
        return '';
    }
    const f = new Date(iso);
    return Number.isNaN(f.getTime()) ? '' : `${MESES[f.getMonth()] ?? ''} de ${String(f.getFullYear())}`;
}

/** Días enteros desde esa fecha hasta hoy. */
export function diasDesde(iso: string, ahora: Date = new Date()): number {
    return Math.floor((ahora.getTime() - new Date(iso).getTime()) / 86_400_000);
}

/** «486,50» y su moneda («€»). */
export function importe(dinero: Importe | null | undefined): { cifra: string; moneda: string } {
    if (dinero == null) {
        return { cifra: '0,00', moneda: '€' };
    }
    const cifra = (dinero.amount / 100).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return { cifra, moneda: dinero.currency === 'EUR' ? '€' : dinero.currency };
}

/** «+34 612 345 678» de un E.164 español; el resto, tal cual. */
export function telefonoLegible(telefono: string | null | undefined): string {
    if (telefono == null || telefono === '') {
        return '';
    }
    const m = /^\+34(\d{3})(\d{3})(\d{3})$/.exec(telefono);
    return m === null ? telefono : `+34 ${m[1] ?? ''} ${m[2] ?? ''} ${m[3] ?? ''}`;
}

/** Iniciales para el avatar («Ana Martín» → «AM»). */
export function inicialesDe(nombre: string): string {
    const p = nombre.trim().split(/\s+/).filter((x) => x !== '');
    const a = p[0]?.charAt(0) ?? '';
    const b = p.length > 1 ? (p[p.length - 1]?.charAt(0) ?? '') : '';
    return (a + b).toLocaleUpperCase('es') || '?';
}

/** ¿Ficha bloqueada por obligación legal (G196)? */
export function estaBloqueada(c: Pick<Cliente, 'status' | 'legal_hold'> | Pick<ResumenCliente, 'status'>): boolean {
    return c.status === 'blocked' || ('legal_hold' in c && c.legal_hold != null);
}

/**
 * Lo que se escribe en «Buscar» (2.1): por nombre, o por el teléfono o el email completos (G42: solo entre los clientes
 * del negocio). Un email va a `email`, un teléfono a `phone` y el resto, desde 2 letras, a `q`.
 */
export function consultaDeBusqueda(texto: string): { q?: string; phone?: string; email?: string } {
    const t = texto.trim();
    if (t.includes('@')) {
        return { email: t.toLowerCase() };
    }
    if (/^\+?[\d\s().-]{6,}$/.test(t)) {
        return { phone: t.replace(/[\s().-]/g, '') };
    }
    return t.length >= 2 ? { q: t } : {};
}

/** Texto de un error para un aviso breve (su `detail` o su título). */
export function textoDeError(e: unknown, porDefecto: string): string {
    return e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : porDefecto;
}

/** Campo del formulario al que apunta un error de la API (`errors[].field`, con o sin `local_`). */
export function campoDeError(campo: string): string {
    const c = campo.replace(/^local_/, '');
    if (c === 'display_name') {
        return 'nombre';
    }
    if (c === 'phone') {
        return 'telefono';
    }
    if (c === 'email') {
        return 'email';
    }
    if (c === 'instagram_username') {
        return 'instagram';
    }
    if (c === 'birth_date') {
        return 'nacimiento';
    }
    if (c === 'birth_date_consent') {
        return 'consentimiento';
    }
    return c.startsWith('field_values.') ? `campo:${c.slice('field_values.'.length)}` : c;
}

/** 409 de un contacto que ya es de otra ficha del negocio (G147): el error del campo. */
export function contactoDeOtro(campo: 'telefono' | 'email' | 'instagram'): string {
    const nombre = campo === 'telefono' ? 'teléfono' : campo === 'email' ? 'email' : 'Instagram';
    return `Ese ${nombre} ya es de otro cliente de tu negocio. Lo verás en Duplicados.`;
}

/** Los errores por campo de un 409 o un 422 de la API, con su campo del formulario. */
export function erroresDeCampos(e: unknown): Record<string, string> {
    if (!(e instanceof ErrorApi) || (e.codigo !== 'conflict' && e.codigo !== 'validation_failed')) {
        return {};
    }
    const fuera: Record<string, string> = {};
    for (const err of e.errores) {
        const campo = campoDeError(err.field);
        if (e.codigo === 'conflict' && (campo === 'telefono' || campo === 'email' || campo === 'instagram')) {
            fuera[campo] = contactoDeOtro(campo);
        } else {
            fuera[campo] = err.message ?? 'Revisa este dato.';
        }
    }
    return fuera;
}

/** El email «válido» a ojo (el servidor decide). */
export function emailValido(v: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/** Instagram sin @ y en minúsculas (G147). */
export function instagramLimpio(v: string): string {
    return v.trim().replace(/^@+/, '').toLowerCase();
}

export function instagramValido(v: string): boolean {
    return /^[a-z0-9._]{1,30}$/.test(v);
}

/** Hoy en `AAAA-MM-DD` (máximo de la fecha de nacimiento). */
export function hoyLocal(ahora: Date = new Date()): string {
    return `${String(ahora.getFullYear())}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(ahora.getDate()).padStart(2, '0')}`;
}
